import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  Fade,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  InputAdornment,
  alpha,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Description as FileIcon,
  ArrowForward as ArrowForwardIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import MDButton from "components/MDButton"; // Assuming you use this wrapper
import PropTypes from "prop-types";

const PersonalInfo = ({
  handleChange,
  handleFileChange,
  handleNext,
  primaryColor,
  bgColor,
  personalInfo,
  // setPersonalInfo is useful here if we want to pre-fill data from the employee lookup
  handleVerification,
  setPersonalInfo,
}) => {
  const [isEmployee, setIsEmployee] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");

  // Local state for the employee verification inputs
  const [employeeCredentials, setEmployeeCredentials] = useState({
    employeeId: "",
    email: "",
  });

  const handleCredentialChange = (prop) => (event) => {
    setEmployeeCredentials({ ...employeeCredentials, [prop]: event.target.value });
    setVerifyError(""); // Clear error on type
  };

  const handleEmployeeVerification = async () => {
    setVerifying(true);
    setVerifyError("");

    ///verify
    await handleVerification(employeeCredentials);

    setVerifying(false);
  };
  return (
    <Fade in={true}>
      <Box>
        {/* --- TOGGLE SWITCH --- */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mb={4}
          p={1}
          bgcolor="white"
          borderRadius={3}
          border={`1px solid ${alpha(primaryColor, 0.1)}`}
          width="fit-content"
          mx="auto"
        >
          <FormControlLabel
            control={
              <Switch
                checked={isEmployee}
                onChange={(e) => {
                  setIsEmployee(e.target.checked);
                  setVerifyError("");
                }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: primaryColor,
                    "&:hover": { backgroundColor: alpha(primaryColor, 0.08) },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: primaryColor,
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" fontWeight="bold" color="text.secondary">
                I am a current employee
              </Typography>
            }
            sx={{ m: 0, px: 2 }}
          />
        </Box>

        {/* --- VIEW 1: CURRENT EMPLOYEE LOGIN --- */}
        {isEmployee ? (
          <Box maxWidth="500px" mx="auto" textAlign="center">
            <Typography variant="h5" gutterBottom>
              Internal Verification
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={4}>
              Please enter your Employee ID and Company Email to proceed directly to the questions.
            </Typography>

            <Grid container spacing={3}>
              {/* Employee ID */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Employee ID"
                  variant="outlined"
                  value={employeeCredentials.employeeId}
                  onChange={handleCredentialChange("employeeId")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon sx={{ color: primaryColor }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryColor,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: primaryColor },
                  }}
                />
              </Grid>

              {/* Employee Email */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="email"
                  label="Company Email"
                  variant="outlined"
                  value={employeeCredentials.email}
                  onChange={handleCredentialChange("email")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: primaryColor }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryColor,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: primaryColor },
                  }}
                />
              </Grid>
            </Grid>

            {/* Error Message */}
            {verifyError && (
              <Fade in>
                <Alert severity="error" sx={{ mt: 3, textAlign: "left" }}>
                  {verifyError}
                </Alert>
              </Fade>
            )}

            {/* Verify Button */}
            <Box mt={4}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1} ml={1}>
                  Upload CV/Resume *
                </Typography>

                <Box
                  component="label"
                  htmlFor="cv-upload-input"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed",
                    borderColor: personalInfo.cv ? primaryColor : "grey.300",
                    backgroundColor: personalInfo.cv ? alpha(primaryColor, 0.05) : "transparent",
                    borderRadius: 2,
                    p: 2,
                    mb: 1,
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: primaryColor,
                      backgroundColor: alpha(primaryColor, 0.05),
                    },
                  }}
                >
                  <input
                    id="cv-upload-input"
                    type="file"
                    accept=".pdf"
                    hidden
                    onChange={handleFileChange}
                    onClick={(e) => (e.target.value = null)}
                  />

                  {personalInfo.cv ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                      maxWidth="400px"
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <CheckCircleIcon sx={{ fontSize: 30, color: primaryColor }} />
                        <Box textAlign="left">
                          <Typography variant="body2" fontWeight="bold" color="text.primary">
                            {personalInfo.cvName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(personalInfo.cv.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPersonalInfo({ ...personalInfo, cv: null, cvName: "" });
                        }}
                      >
                        <FileIcon color="error" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={2}>
                      <CloudUploadIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                      <Box textAlign="left">
                        <Typography variant="body2" color="text.primary" fontWeight="medium">
                          Click to upload CV
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PDF (Max 5MB)
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
              <MDButton
                variant="contained"
                size="large"
                fullWidth
                onClick={handleEmployeeVerification}
                disabled={
                  verifying || !employeeCredentials.employeeId || !employeeCredentials.email
                }
                sx={{
                  bgcolor: primaryColor,
                  color: "white !important", // Ensure text is white
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: `0 4px 14px ${alpha(primaryColor, 0.4)}`,
                  "&:hover": { bgcolor: primaryColor, filter: "brightness(0.9)" },
                  "&:disabled": { bgcolor: "grey.300" },
                }}
              >
                {verifying ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <>
                    Verify & Proceed <ArrowForwardIcon sx={{ ml: 1 }} />
                  </>
                )}
              </MDButton>
            </Box>
          </Box>
        ) : (
          /* --- VIEW 2: EXTERNAL APPLICANT FORM (Existing) --- */
          <>
            <Grid container spacing={2}>
              {/* Full Name */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Full Name"
                  variant="outlined"
                  value={personalInfo.fullName}
                  onChange={handleChange("fullName")}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryColor,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: primaryColor },
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="Email Address"
                  variant="outlined"
                  value={personalInfo.email}
                  onChange={handleChange("email")}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryColor,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: primaryColor },
                  }}
                />
              </Grid>

              {/* Phone */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="tel"
                  label="Phone Number"
                  variant="outlined"
                  value={personalInfo.phone}
                  onChange={handleChange("phone")}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: primaryColor,
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: primaryColor },
                  }}
                />
              </Grid>

              {/* CV Upload */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1} ml={1}>
                  Upload CV/Resume *
                </Typography>

                <Box
                  component="label"
                  htmlFor="cv-upload-input"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed",
                    borderColor: personalInfo.cv ? primaryColor : "grey.300",
                    backgroundColor: personalInfo.cv ? alpha(primaryColor, 0.05) : "transparent",
                    borderRadius: 2,
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: primaryColor,
                      backgroundColor: alpha(primaryColor, 0.05),
                    },
                  }}
                >
                  <input
                    id="cv-upload-input"
                    type="file"
                    accept=".pdf"
                    hidden
                    onChange={handleFileChange}
                    onClick={(e) => (e.target.value = null)}
                  />

                  {personalInfo.cv ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                      maxWidth="400px"
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <CheckCircleIcon sx={{ fontSize: 30, color: primaryColor }} />
                        <Box textAlign="left">
                          <Typography variant="body2" fontWeight="bold" color="text.primary">
                            {personalInfo.cvName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(personalInfo.cv.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPersonalInfo({ ...personalInfo, cv: null, cvName: "" });
                        }}
                      >
                        <FileIcon color="error" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={2}>
                      <CloudUploadIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                      <Box textAlign="left">
                        <Typography variant="body2" color="text.primary" fontWeight="medium">
                          Click to upload CV
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PDF (Max 5MB)
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Navigation Button (Next) */}
            <Box mt={4} display="flex" justifyContent="flex-end">
              <MDButton
                variant="contained"
                size="medium"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  color: "white !important", // Ensure text is white
                  bgcolor: primaryColor,
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: `0 4px 14px ${alpha(primaryColor, 0.4)}`,
                  "&:hover": {
                    bgcolor: primaryColor,
                    filter: "brightness(0.9)",
                  },
                }}
              >
                Next Step
              </MDButton>
            </Box>
          </>
        )}
      </Box>
    </Fade>
  );
};

PersonalInfo.propTypes = {
  handleChange: PropTypes.func,
  handleFileChange: PropTypes.func,
  handleNext: PropTypes.func,
  primaryColor: PropTypes.string,
  bgColor: PropTypes.string,
  activeStep: PropTypes.number,
  personalInfo: PropTypes.object,
  handleVerification: PropTypes.func,
  setPersonalInfo: PropTypes.func,
};

export default PersonalInfo;
